import { API_CONFIG } from '../config';
import { getParachuteAuthToken } from '../utils/parachuteAuthToken';
import {
  createWorkspaceLawFirmUser,
  fetchWorkspaceLawFirmUsers,
  normalizeLawFirmUser,
  resetWorkspaceLawFirmUserPassword,
  updateWorkspaceLawFirmUser,
  type LawFirmUserRow,
  type UserOnboardingInvite,
} from './ClientLicensesApiClient';
import {
  inviteCompanyUser,
  fetchCompanyUsers,
  resetCompanyUserPassword,
  updateCompanyUser,
} from './FirmCompaniesApiClient';

export type { LawFirmUserRow, UserOnboardingInvite };

export type ManagedDirectoryCreateInput = {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  firmRole?: 'Admin' | 'Staff';
};

export type ManagedDirectoryUpdateInput = {
  firstName: string;
  lastName: string;
  phone?: string;
  isActive: boolean;
  firmRole?: 'Admin' | 'Staff';
};

export type ManagedDirectoryUsersAdapter = {
  list: () => Promise<LawFirmUserRow[]>;
  create: (input: ManagedDirectoryCreateInput) => Promise<{ user: LawFirmUserRow; invite: UserOnboardingInvite }>;
  update: (userId: number, input: ManagedDirectoryUpdateInput) => Promise<LawFirmUserRow>;
  resetPassword: (userId: number) => Promise<{ passwordResetLink: string }>;
};

function authHeaders(): HeadersInit {
  const token = getParachuteAuthToken();
  return {
    accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const NO_STORE: RequestInit = { cache: 'no-store' };

function parseInviteEnvelope(raw: Record<string, unknown>): { user: LawFirmUserRow; invite: UserOnboardingInvite } {
  const userRaw = raw.user ?? raw.User;
  const invRaw = raw.invite ?? raw.Invite;
  if (!userRaw || typeof userRaw !== 'object' || !invRaw || typeof invRaw !== 'object') {
    throw new Error('Invalid invite response');
  }
  const inv = invRaw as Record<string, unknown>;
  const tp = inv.temporaryPassword ?? inv.TemporaryPassword;
  const pl = inv.passwordResetLink ?? inv.PasswordResetLink;
  if (typeof tp !== 'string' || typeof pl !== 'string') {
    throw new Error('Invalid invite response');
  }
  return {
    user: normalizeLawFirmUser(userRaw as Record<string, unknown>),
    invite: { temporaryPassword: tp, passwordResetLink: pl },
  };
}

/** Platform Root users (`/api/pct/v1/platform/users`). */
export function createPlatformRootUsersAdapter(): ManagedDirectoryUsersAdapter {
  const base = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PLATFORM_ROOT_USERS}`;
  const updateUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PLATFORM_ROOT_USERS_UPDATE}`;
  const resetUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PLATFORM_ROOT_USERS_RESET_PASSWORD}`;

  return {
    list: async () => {
      const response = await fetch(base, { ...NO_STORE, method: 'GET', headers: authHeaders() });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || `Failed to load platform users (${response.status})`);
      }
      const json = (await response.json()) as { data?: unknown };
      const rows = Array.isArray(json?.data) ? json.data : [];
      return rows.map((r) =>
        typeof r === 'object' && r !== null
          ? normalizeLawFirmUser(r as Record<string, unknown>)
          : normalizeLawFirmUser({})
      );
    },
    create: async (input) => {
      const response = await fetch(base, {
        ...NO_STORE,
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: input.email.trim(),
          firstName: input.firstName.trim(),
          lastName: input.lastName.trim(),
          ...(input.phone != null && String(input.phone).trim() !== '' ? { phone: String(input.phone).trim() } : {}),
        }),
      });
      if (!response.ok) {
        const errJson = (await response.json().catch(() => null)) as Record<string, unknown> | null;
        let msg = `Failed to invite platform user (${response.status})`;
        if (errJson && typeof errJson === 'object' && 'message' in errJson && errJson.message != null) {
          msg = String(errJson.message);
        } else if (errJson && typeof errJson === 'object' && 'title' in errJson && errJson.title != null) {
          msg = String(errJson.title);
        }
        throw new Error(msg);
      }
      const json = (await response.json()) as { data?: Record<string, unknown> };
      const raw = json?.data;
      if (!raw || typeof raw !== 'object') {
        throw new Error('Invalid invite response');
      }
      return parseInviteEnvelope(raw);
    },
    update: async (userId, input) => {
      const response = await fetch(updateUrl, {
        ...NO_STORE,
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: userId,
          firstName: input.firstName.trim(),
          lastName: input.lastName.trim(),
          isActive: input.isActive,
          ...(input.phone != null && String(input.phone).trim() !== '' ? { phone: String(input.phone).trim() } : {}),
        }),
      });
      if (!response.ok) {
        const errJson = (await response.json().catch(() => null)) as Record<string, unknown> | null;
        let msg = `Failed to update user (${response.status})`;
        if (errJson && typeof errJson === 'object' && 'message' in errJson && errJson.message != null) {
          msg = String(errJson.message);
        }
        throw new Error(msg);
      }
      const json = (await response.json()) as { data?: Record<string, unknown> };
      const raw = json?.data;
      if (!raw || typeof raw !== 'object') {
        throw new Error('Invalid update user response');
      }
      return normalizeLawFirmUser(raw);
    },
    resetPassword: async (userId) => {
      const response = await fetch(resetUrl, {
        ...NO_STORE,
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      if (!response.ok) {
        const errJson = (await response.json().catch(() => null)) as Record<string, unknown> | null;
        let msg = `Failed to issue reset link (${response.status})`;
        if (errJson && typeof errJson === 'object' && 'message' in errJson && errJson.message != null) {
          msg = String(errJson.message);
        }
        throw new Error(msg);
      }
      const json = (await response.json()) as { data?: Record<string, unknown> };
      const raw = json?.data;
      if (!raw || typeof raw !== 'object') {
        throw new Error('Invalid reset link response');
      }
      const link = raw.passwordResetLink ?? raw.PasswordResetLink;
      if (typeof link !== 'string') {
        throw new Error('Invalid reset link response');
      }
      return { passwordResetLink: link };
    },
  };
}

/** Firm workspace users (JWT-scoped law firm). */
export function createFirmWorkspaceUsersAdapter(): ManagedDirectoryUsersAdapter {
  return {
    list: () => fetchWorkspaceLawFirmUsers(),
    create: (input) => {
      const role = input.firmRole ?? 'Staff';
      return createWorkspaceLawFirmUser({
        email: input.email,
        firstName: input.firstName,
        lastName: input.lastName,
        phone: input.phone,
        firmRole: role,
      }).then(({ user, invite }) => ({ user, invite }));
    },
    update: (userId, input) => {
      const role = input.firmRole ?? 'Staff';
      return updateWorkspaceLawFirmUser(userId, {
        firstName: input.firstName,
        lastName: input.lastName,
        phone: input.phone,
        firmRole: role,
        isActive: input.isActive,
      });
    },
    resetPassword: (userId) => resetWorkspaceLawFirmUserPassword(userId),
  };
}

/** Client users for one company (firm admin; path includes law firm + company id). */
export function createCompanyClientUsersAdapter(lawFirmId: string, companyId: string): ManagedDirectoryUsersAdapter {
  return {
    list: () => fetchCompanyUsers(lawFirmId, companyId),
    create: (input) =>
      inviteCompanyUser(lawFirmId, companyId, {
        email: input.email,
        firstName: input.firstName,
        lastName: input.lastName,
        phone: input.phone,
      }).then(({ user, invite }) => ({ user, invite })),
    update: (userId, input) =>
      updateCompanyUser(lawFirmId, companyId, {
        id: userId,
        firstName: input.firstName,
        lastName: input.lastName,
        phone: input.phone,
        isActive: input.isActive,
      }),
    resetPassword: (userId) => resetCompanyUserPassword(lawFirmId, companyId, userId),
  };
}
